import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Lock, Timer, Eye, RefreshCw, CheckCircle, XCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLockedDeals = () => {
  const { getToken } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDeal, setSelectedDeal] = useState(null);

  useEffect(() => {
    fetchDeals();
  }, [statusFilter]);

  const fetchDeals = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/locked-deals${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDeals(response.data);
    } catch (error) {
      toast.error("Failed to load locked deals");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (dealId, newStatus) => {
    try {
      await axios.put(`${API}/admin/locked-deals/${dealId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Deal marked as ${newStatus}`);
      fetchDeals();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Active: "bg-green-100 text-green-700",
      Expired: "bg-gray-100 text-gray-700",
      Converted: "bg-blue-100 text-blue-700"
    };
    const icons = {
      Active: <Clock className="w-3 h-3" />,
      Expired: <XCircle className="w-3 h-3" />,
      Converted: <CheckCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  const getTimeRemaining = (expiryStr) => {
    const expiry = new Date(expiryStr);
    const now = new Date();
    const diff = expiry - now;
    
    if (diff <= 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <AdminLayout title="Locked Deals">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Locked Deals">
      <div className="space-y-6" data-testid="locked-deals-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Lock className="w-6 h-6 text-[#B08968]" />
            <p className="text-muted-foreground">
              {deals.length} deal{deals.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Deals Table */}
        {deals.length > 0 ? (
          <div className="bg-white rounded-2xl border border-border/40 shadow-elegant overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Locked Amount</th>
                    <th>Time Remaining</th>
                    <th>Status</th>
                    <th>Locked At</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id} data-testid={`deal-row-${deal.id}`}>
                      <td>
                        <div>
                          <p className="font-medium text-[#2D2D2D]">{deal.user_name}</p>
                          <p className="text-xs text-muted-foreground">{deal.user_email}</p>
                        </div>
                      </td>
                      <td>
                        <span className="font-semibold text-[#B08968]">
                          {formatINR(deal.grand_total)}
                        </span>
                      </td>
                      <td>
                        {deal.status === "Active" ? (
                          <span className="flex items-center gap-1 text-sm text-green-600">
                            <Timer className="w-4 h-4" />
                            {getTimeRemaining(deal.lock_expiry)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td>{getStatusBadge(deal.status)}</td>
                      <td className="text-sm text-muted-foreground">
                        {formatDate(deal.lock_start)}
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setSelectedDeal(deal)}
                            data-testid={`view-deal-${deal.id}`}
                            className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {deal.status === "Active" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateStatus(deal.id, "Converted")}
                              className="text-xs"
                            >
                              Mark Converted
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-border/40 p-12 text-center">
            <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              No locked deals found.
            </p>
          </div>
        )}
      </div>

      {/* Deal Details Modal */}
      <Dialog open={!!selectedDeal} onOpenChange={() => setSelectedDeal(null)}>
        <DialogContent className="max-w-2xl" data-testid="deal-details-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Locked Deal Details</DialogTitle>
          </DialogHeader>
          
          {selectedDeal && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Customer Name</p>
                  <p className="font-medium">{selectedDeal.user_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</p>
                  <p className="font-medium">{selectedDeal.user_email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Phone</p>
                  <p className="font-medium">{selectedDeal.user_phone || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                  {getStatusBadge(selectedDeal.status)}
                </div>
              </div>
              
              {/* Lock Timing */}
              <div className="bg-secondary/30 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Lock Start</p>
                    <p className="font-medium">{formatDate(selectedDeal.lock_start)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Lock Expiry</p>
                    <p className="font-medium">{formatDate(selectedDeal.lock_expiry)}</p>
                  </div>
                </div>
              </div>
              
              {/* Configuration */}
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Configuration</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between bg-secondary/20 p-2 rounded">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium">{selectedDeal.inputs_snapshot?.duration_hours || 48} hours</span>
                  </div>
                  <div className="flex justify-between bg-secondary/20 p-2 rounded">
                    <span className="text-muted-foreground">Guest Count</span>
                    <span className="font-medium">{selectedDeal.inputs_snapshot?.guest_count || 0} pax</span>
                  </div>
                  <div className="flex justify-between bg-secondary/20 p-2 rounded">
                    <span className="text-muted-foreground">Food Included</span>
                    <span className="font-medium">{selectedDeal.inputs_snapshot?.food_included ? "Yes" : "No"}</span>
                  </div>
                  <div className="flex justify-between bg-secondary/20 p-2 rounded">
                    <span className="text-muted-foreground">Payment Mode</span>
                    <span className="font-medium">{selectedDeal.inputs_snapshot?.payment_mode || 50}% advance</span>
                  </div>
                </div>
              </div>
              
              {/* Locked Amount */}
              <div className="bg-[#B08968]/10 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Deal Value</p>
                    <p className="text-lg font-medium">{formatINR(selectedDeal.locked_deal_value)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Locked Total</p>
                    <p className="text-2xl font-bold text-[#B08968]">{formatINR(selectedDeal.grand_total)}</p>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              {selectedDeal.status === "Active" && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      updateStatus(selectedDeal.id, "Converted");
                      setSelectedDeal(null);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Converted
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      updateStatus(selectedDeal.id, "Expired");
                      setSelectedDeal(null);
                    }}
                    className="flex-1"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Mark as Expired
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminLockedDeals;
